import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout';
import { MapPin, Mail, Phone, Users, ExternalLink, Check } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'react-toastify';

const ReginaPage = () => {
  const [mayorData, setMayorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubscribeForm, setShowSubscribeForm] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    opted_in_mayor_emails: true,
    opted_in_cyqr_emails: true
  });

  useEffect(() => {
    fetchMayorData();
  }, []);

  const fetchMayorData = async () => {
    try {
      const response = await api.get('/regina');
      setMayorData(response.data);
    } catch (error) {
      console.error('Error fetching mayor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await api.post('/regina/subscribe', {
        name: formData.name,
        email: formData.email,
        opted_in_mayor_emails: formData.opted_in_mayor_emails,
        opted_in_cyqr_emails: formData.opted_in_cyqr_emails
      });
      setSubscribed(true);
      toast.success('Thank you for subscribing!');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  const mayor = mayorData?.mayor;
  const content = mayorData?.content;
  const subscriberCount = mayorData?.subscriber_count || 0;

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-b from-[#0a0a0a] to-black border-b border-gray-800">
          <div className="max-w-4xl mx-auto px-4 py-16 text-center">
            <div className="flex justify-center mb-6">
              <MapPin className="w-12 h-12 text-[#C5A52A]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-4 font-mono">
              {content?.custom_headline || (
                <>REGINA</>
              )}
            </h1>
            
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {content?.custom_description || "Connect with Regina's municipal leadership. Stay informed about city-wide initiatives, policies, and community updates."}
            </p>

            <div className="flex items-center justify-center gap-4 mt-6 text-gray-500 text-sm font-mono">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {subscriberCount} subscribers
              </span>
            </div>
          </div>
        </div>

        {/* Mayor Info */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {mayor ? (
            <div className="grid md:grid-cols-3 gap-8">
              {/* Mayor Profile */}
              <div className="md:col-span-1">
                <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden sticky top-4">
                  {mayor.image_url && (
                    <img 
                      src={mayor.image_url} 
                      alt={mayor.name}
                      className="w-full aspect-square object-cover"
                    />
                  )}
                  <div className="p-4">
                    <h2 className="text-xl font-bold text-white">{mayor.name}</h2>
                    <p className="text-[#C5A52A] font-mono text-sm mt-1">Mayor of Regina</p>
                    
                    {/* Contact Info */}
                    <div className="mt-4 space-y-2 text-sm">
                      {mayor.email && (
                        <a 
                          href={`mailto:${mayor.email}`}
                          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          {mayor.email}
                        </a>
                      )}
                      {mayor.phone && (
                        <a 
                          href={`tel:${mayor.phone}`}
                          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                          {mayor.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="md:col-span-2 space-y-8">
                {/* Bio */}
                {(content?.councillor_bio_override || mayor.bio) && (
                  <div>
                    <h3 className="font-mono text-sm uppercase tracking-widest text-gray-400 mb-4">About the Mayor</h3>
                    <div className="prose prose-invert max-w-none">
                      <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {content?.councillor_bio_override || mayor.bio}
                      </p>
                    </div>
                  </div>
                )}

                {/* Priorities */}
                {content?.priorities && (
                  <div>
                    <h3 className="font-mono text-sm uppercase tracking-widest text-gray-400 mb-4">Current Priorities</h3>
                    <div className="flex flex-wrap gap-2">
                      {(typeof content.priorities === 'string' ? content.priorities.split(',') : content.priorities).map((priority, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-[#C5A52A]/20 text-[#C5A52A] border border-[#C5A52A]/30 rounded text-sm"
                        >
                          {priority.trim ? priority.trim() : priority}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Mayor information coming soon.
            </div>
          )}

          {/* Subscribe Section */}
          <div className="mt-16 border border-gray-800 bg-gray-900/50 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Stay Informed</h3>
            <p className="text-gray-400 mb-6">
              Get city-wide updates and municipal news delivered to your inbox.
            </p>

            {subscribed ? (
              <div className="flex items-center justify-center gap-2 text-green-400">
                <Check className="w-5 h-5" />
                <span>You're subscribed! Check your email for confirmation.</span>
              </div>
            ) : !showSubscribeForm ? (
              <button
                onClick={() => setShowSubscribeForm(true)}
                className="px-8 py-3 bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold rounded transition-colors"
              >
                Subscribe for Updates
              </button>
            ) : (
              <div className="max-w-md mx-auto">
                <form onSubmit={handleSubscribe} className="bg-gray-900 border border-gray-700 rounded p-6 text-left">
                  <div className="mb-4">
                    <label className="block text-sm font-mono uppercase tracking-widest text-gray-400 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A52A] focus:outline-none"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-mono uppercase tracking-widest text-gray-400 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:border-[#C5A52A] focus:outline-none"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="mb-4 space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.opted_in_mayor_emails}
                        onChange={(e) => setFormData({ ...formData, opted_in_mayor_emails: e.target.checked })}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-300">
                        Receive updates from the Mayor's office
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.opted_in_cyqr_emails}
                        onChange={(e) => setFormData({ ...formData, opted_in_cyqr_emails: e.target.checked })}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-300">
                        Receive CYQR platform updates and community news
                      </span>
                    </label>
                  </div>

                  <p className="text-xs text-gray-500 mb-4">
                    By subscribing, you agree to our{' '}
                    <a href="/terms" className="text-[#C5A52A] hover:underline">Terms of Service</a>.
                    You can unsubscribe at any time.
                  </p>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={subscribing}
                      className="flex-1 bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold px-6 py-3 rounded transition-colors disabled:opacity-50"
                    >
                      {subscribing ? 'Subscribing...' : 'Subscribe'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowSubscribeForm(false)}
                      className="px-6 py-3 border border-gray-700 hover:border-gray-500 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* About Section */}
          {content?.about_ward && (
            <div className="mt-12 border border-gray-800 bg-gray-900 rounded-lg p-6">
              <h3 className="font-mono text-sm uppercase tracking-widest text-gray-400 mb-4">About Regina</h3>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">
                {content.about_ward}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ReginaPage;
